import { createContext, useContext, useMemo, useCallback, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useSchool } from './SchoolContext';
import { extractSubdomain } from '../utils/auth';
import * as urlUtils from '../utils/urlUtils';

// Create a context to hold URL generation functions
export const UrlContext = createContext({});

export const UrlProvider = ({ children }) => {
  // Get current subdomain from SchoolContext
  const { school } = useSchool();
  
  // Get the current subdomain from Redux for redundancy
  const reduxSubdomain = useSelector(state => state.language.currentSubdomain);
  
  // Add a local state variable to force re-renders when subdomain changes
  const [refreshTrigger, setRefreshTrigger] = useState(Date.now());
  
  // Listen for subdomain change events
  useEffect(() => {
    // Handler for subdomain change events
    const handleSubdomainChanged = (event) => {
      console.log('[UrlContext] Detected subdomain change event:', 
                  event.detail.oldSubdomain, '->', event.detail.newSubdomain);
      
      // Force a re-render of this component
      setRefreshTrigger(Date.now());
    };
    
    // Register event listener
    window.addEventListener('subdomain-changed', handleSubdomainChanged);
    
    // Check sessionStorage for forceRefreshTimestamp on mount
    const forceRefresh = sessionStorage.getItem('forceRefreshTimestamp');
    if (forceRefresh) {
      console.log('[UrlContext] Detected force refresh flag, refreshing URL context');
      sessionStorage.removeItem('forceRefreshTimestamp');
      setRefreshTrigger(Date.now());
    }
    
    // Cleanup
    return () => {
      window.removeEventListener('subdomain-changed', handleSubdomainChanged);
    };
  }, []);
  
  // Create memoized URL generation functions that will update whenever the subdomain changes
  const getBaseUrl = useCallback((overrideSubdomain) => {
    // Force this to always log the current values to help with debugging
    console.log(`[UrlContext] getBaseUrl called with override=${overrideSubdomain}, school=${school?.subdomain}, redux=${reduxSubdomain}, refreshTrigger=${refreshTrigger}`);
    
    // Prioritize override, then school context, then redux, then extract from URL
    const subdomain = overrideSubdomain || 
                      (school && school.subdomain) || 
                      reduxSubdomain || 
                      extractSubdomain();
    
    return urlUtils.getBaseUrl(subdomain);
  }, [school, reduxSubdomain, refreshTrigger]);
  
  const getAdminUrl = useCallback((overrideSubdomain) => {
    const subdomain = overrideSubdomain || 
                      (school && school.subdomain) || 
                      reduxSubdomain || 
                      extractSubdomain();
    
    return urlUtils.getAdminUrl(subdomain);
  }, [school, reduxSubdomain, refreshTrigger]);
  
  const getMenuUrl = useCallback((menuId, overrideSubdomain) => {
    const subdomain = overrideSubdomain || 
                      (school && school.subdomain) || 
                      reduxSubdomain || 
                      extractSubdomain();
    
    return urlUtils.getMenuUrl(subdomain, menuId);
  }, [school, reduxSubdomain, refreshTrigger]);
  
  const getPageUrl = useCallback((page, overrideSubdomain) => {
    const subdomain = overrideSubdomain || 
                      (school && school.subdomain) || 
                      reduxSubdomain || 
                      extractSubdomain();
    
    return urlUtils.getPageUrl(subdomain, page);
  }, [school, reduxSubdomain, refreshTrigger]);
  
  const getMenuEditorUrl = useCallback((menuId, queryParams, overrideSubdomain) => {
    const subdomain = overrideSubdomain || 
                      (school && school.subdomain) || 
                      reduxSubdomain || 
                      extractSubdomain();
    
    return urlUtils.getMenuEditorUrl(subdomain, menuId, queryParams);
  }, [school, reduxSubdomain, refreshTrigger]);
  
  // Get the public site URL with subdomain preserved - dynamic version
  const getPublicSiteUrl = useCallback(() => {
    const subdomain = (school && school.subdomain) || 
                      reduxSubdomain || 
                      extractSubdomain();
    
    if (!subdomain) {
      return '/';
    }
    
    return `/${subdomain}`;
  }, [school, reduxSubdomain, refreshTrigger]);
  
  const getLoginUrl = useCallback(() => {
    const subdomain = (school && school.subdomain) || 
                      reduxSubdomain || 
                      extractSubdomain();
    
    if (subdomain) {
      return `/${subdomain}/login`;
    }
    
    return '/login';
  }, [school, reduxSubdomain, refreshTrigger]);
  
  // Memoize the context value to prevent unnecessary re-renders
  const urlContextValue = useMemo(() => ({
    getBaseUrl,
    getAdminUrl,
    getMenuUrl,
    getPageUrl,
    getMenuEditorUrl,
    getPublicSiteUrl,
    getLoginUrl
  }), [
    getBaseUrl, 
    getAdminUrl, 
    getMenuUrl, 
    getPageUrl, 
    getMenuEditorUrl, 
    getPublicSiteUrl,
    getLoginUrl
  ]);
  
  return (
    <UrlContext.Provider value={urlContextValue}>
      {children}
    </UrlContext.Provider>
  );
};

// Custom hook to use URL context
export const useUrl = () => useContext(UrlContext); 